import type {
  Plugin,
  EntityPluginContext,
  EntityTypeConfig,
  DataSource,
} from "@brains/plugins";
import { EntityPlugin, emptyEntityPluginConfigSchema } from "@brains/plugins";
import { ENTITY_CHANNELS, DIRECTORY_SYNC_CHANNELS } from "@brains/contracts";
import { isPlainRecord } from "@brains/utils/predicates";
import { readString } from "@brains/utils/record-fields";
import {
  SITE_METADATA_GET_CHANNEL,
  SITE_METADATA_UPDATED_CHANNEL,
} from "@brains/site-composition";
import {
  siteInfoSchema,
  type SiteInfoEntity,
  type SiteInfoBody,
} from "./schemas/site-info-schema";
import { SiteInfoAdapter } from "./adapters/site-info-adapter";
import { SiteInfoService } from "./services/site-info-service";
import { SiteInfoDataSource } from "./datasources/site-info-datasource";
import packageJson from "../package.json";

const siteInfoAdapter: SiteInfoAdapter = new SiteInfoAdapter();
const siteInfoEntityType = "site-info";

/**
 * Site-info EntityPlugin — manages the site's metadata (title, description, CTA, etc.).
 *
 * Singleton entity (id: "site-info"). Created with defaults on first boot.
 * Zero tools — edited via system_update or Studio.
 */
export class SiteInfoPlugin extends EntityPlugin<
  SiteInfoEntity,
  Record<string, never>,
  Record<string, never>
> {
  readonly entityType: typeof siteInfoEntityType = siteInfoEntityType;
  readonly schema: typeof siteInfoSchema = siteInfoSchema;
  readonly adapter: SiteInfoAdapter = siteInfoAdapter;

  private defaultSiteInfo: Partial<SiteInfoBody>;

  constructor(config?: { siteInfo?: Partial<SiteInfoBody> }) {
    super("site-info", packageJson, {}, emptyEntityPluginConfigSchema);
    this.defaultSiteInfo = config?.siteInfo ?? {};
  }

  protected override getEntityTypeConfig(): EntityTypeConfig | undefined {
    return {
      embeddable: false,
      projectionSource: false,
      projectionSourceRole: "excluded",
    };
  }

  protected override getDataSources(): DataSource[] {
    return [new SiteInfoDataSource(this.logger.child("SiteInfoDataSource"))];
  }

  protected override async onRegister(
    context: EntityPluginContext,
  ): Promise<void> {
    const service = SiteInfoService.createFresh(
      context.entityService,
      this.logger,
      this.defaultSiteInfo,
      {
        getBrain: context.identity.get,
        getAnchor: context.identity.getProfile,
      },
    );

    context.messaging.subscribeExecution(
      SITE_METADATA_GET_CHANNEL,
      async () => {
        const siteInfo = await service.getSiteInfo();
        return { success: true, data: siteInfo };
      },
    );

    context.messaging.subscribe(ENTITY_CHANNELS.updated, async (message) => {
      const payload = isPlainRecord(message.payload)
        ? message.payload
        : undefined;
      if (readString(payload, "entityType") === "site-info") {
        const siteInfo = await service.getSiteInfo();
        await context.messaging.send({
          type: SITE_METADATA_UPDATED_CHANNEL,
          payload: siteInfo,
          broadcast: true,
        });
      }
      return { success: true };
    });

    // Create default entity after seed content is imported
    context.messaging.subscribe(
      DIRECTORY_SYNC_CHANNELS.initialCompleted,
      async () => {
        await service.initialize();
        return { success: true };
      },
    );
  }
}

export function siteInfoPlugin(config?: {
  siteInfo?: Partial<SiteInfoBody>;
}): Plugin {
  return new SiteInfoPlugin(config);
}
